import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ownerToFormValue, ownerToPayload } from '../../formModel';
import { emptyOwnerFormValue, type OwnerFormValue } from '../../formSchema';
import { useOwnerMutations, useOwnerQuery } from '../../hooks';
import { OwnerEditorForm } from './OwnerEditorForm';

export function OwnerEditorPage(props: { ownerId?: string | undefined }) {
  const { returnTo } = useReturnNavigation({ to: '/demand/owners' });
  const editing = Boolean(props.ownerId);
  const query = useOwnerQuery(props.ownerId ?? '');
  const mutations = useOwnerMutations();
  const saving = mutations.create.isPending || mutations.update.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const initialValue = query.data ? ownerToFormValue(query.data) : emptyOwnerFormValue;

  async function save(value: OwnerFormValue) {
    try {
      const payload = ownerToPayload(value, {
        originalTcfVendorId: query.data?.tcfVendorId,
      });
      if (props.ownerId) {
        await mutations.update.mutateAsync({ id: props.ownerId, payload });
      } else {
        await mutations.create.mutateAsync(payload);
      }
      toast.success(dashboardT(editing ? 'owners.updated' : 'owners.created'));
      await returnTo({ replace: true });
    } catch (error) {
      showRequestError(error, dashboardT('owners.saveError'));
    }
  }

  let content;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('owners.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('owners.detailsError')}>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('common.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else {
    content = (
      <OwnerEditorForm
        key={props.ownerId ?? 'create'}
        initialValue={initialValue}
        saving={saving}
        onCancel={() => void returnTo()}
        onSubmit={save}
      />
    );
  }

  return (
    <DashboardPageFormShell
      title={dashboardT(editing ? 'owners.editTitle' : 'owners.createTitle')}
      backAction={
        <ReturnBackLink fallback={{ to: '/demand/owners' }} label={dashboardT('owners.back')} />
      }
    >
      {content}
    </DashboardPageFormShell>
  );
}
